import React, {useEffect, useRef, useState} from "react";
import {MedicationV2, MedicationVariation, OptionDimensionValue} from "../../types";
import MedicationPriceCardV2 from "./medication-price-card-v2";
import {findCurrentSelectionPrices, findLeastStrengthDrugId, removeWordSupplyInStringIfAny} from "../../../utils/med-widget-utils";

export interface MedicationOptionsCardProps {
    medication: MedicationV2;
}

const generateDropdownItemsForStrength = (drugIDToVariations: Record<string, MedicationVariation>) => {
    const dropdownItems: JSX.Element[] = [];
    Object.keys(drugIDToVariations).forEach(key => dropdownItems.push(
        <pui-dropdown-item-two type="option" label={drugIDToVariations[key].strength} value={key}></pui-dropdown-item-two>));
    return dropdownItems;
}

const generateDropdownItemsForOptions = (optionDimensionValues: OptionDimensionValue[]) => {
    const dropdownItems: JSX.Element[] = [];
    optionDimensionValues.forEach((value, index) => {
        dropdownItems.push(<pui-dropdown-item-two type="option" label={value.displayString} value={index}></pui-dropdown-item-two>);
    })
    return dropdownItems;
}

const MedicationCardOptions = ({medication}: MedicationOptionsCardProps) => {
    const drugIDToVariations = medication.drugIDToVariations;

    const drugOptionRef = useRef();
    const quantityOptionRef = useRef();
    const supplyOptionRef = useRef();

    //represent 3 dropdowns we have
    const [selectedDrugId, setSelectedDrugId] = useState<string>(
        //Lazy load so it only executes when initially mounted.
        () => findLeastStrengthDrugId(drugIDToVariations));
    const [selectedQuantityIndex, setSelectedQuantityIndex] = useState<number>(0);
    const [selectedSupplyIndex, setSelectedSupplyIndex] = useState<number>(0);
    //Save selectedDrugId state as a Ref so that it can be correctly retrieved in event handler
    const drugIdRef = useRef('');
    drugIdRef.current = selectedDrugId;

    const medicationPricing = findCurrentSelectionPrices(medication, selectedDrugId, selectedQuantityIndex, selectedSupplyIndex);

    useEffect(() => {
        // use an any as we don't have typings for PUI elements :(
        const drugEl: any = drugOptionRef.current;
        if (drugEl) {
            drugEl.onSelect = (newValue: string) => {
                //If newly selected drugID is different, then reset quantity and supply.
                if (newValue !== drugIdRef.current) {
                    setSelectedQuantityIndex(0);
                    setSelectedSupplyIndex(0);
                }
                setSelectedDrugId(newValue);
            }
        }

        const quantityEl: any = quantityOptionRef.current;
        if (quantityEl) {
            quantityEl.onSelect = (newValue: number) => {
                setSelectedQuantityIndex(newValue);
            }
        }

        const supplyEl: any = supplyOptionRef.current;
        if (supplyEl) {
            supplyEl.onSelect = (newValue: number) => {
                setSelectedSupplyIndex(newValue);
            }
        }
    }, []);

    return (
        <>
            <pui-dropdown-two id="drugId-input" class="medication-options-dropdown" name="drugId" spacingleft="med-small" spacingright="med-small"
                              label="STRENGTH" disableShowOptionsIfSingle
                              selectedText={drugIDToVariations[selectedDrugId].strength} value={selectedDrugId} ref={drugOptionRef}>
                {generateDropdownItemsForStrength(drugIDToVariations)}
            </pui-dropdown-two>
            <pui-dropdown-two id="quantityPer30Days-input" class="medication-options-dropdown" name="quantityPer30Days" spacingleft="med-small" spacingright="med-small"
                              label={medication.dimensionKeys[0].displayString.toUpperCase()} disableShowOptionsIfSingle
                              selectedText={medication.dimensionValues[0][selectedQuantityIndex].displayString} value={selectedQuantityIndex}
                              ref={quantityOptionRef}>
                {generateDropdownItemsForOptions(medication.dimensionValues[0])}
            </pui-dropdown-two>
            <pui-dropdown-two id="supply-input" class="medication-options-dropdown" name="supply" spacingleft="med-small" spacingright="med-small"
                              label={medication.dimensionKeys[1].displayString.toUpperCase()} disableShowOptionsIfSingle
                              selectedText={removeWordSupplyInStringIfAny(medication.dimensionValues[1][selectedSupplyIndex].displayString)}
                              value={selectedSupplyIndex}
                              ref={supplyOptionRef}>
                {generateDropdownItemsForOptions(medication.dimensionValues[1])}
            </pui-dropdown-two>
            <MedicationPriceCardV2 perPillPrimePrice={medicationPricing?.perPillPrimePrice} totalPrimePrice={medicationPricing?.totalPrimePrice}
                                   totalRetailPrice={medicationPricing?.totalRetailPrice}/>
        </>
    )
}

export default MedicationCardOptions;
